import type { MentionEntity } from '@terra/shared-dto-chat-types';
import type { MentionEntityWithOffset } from '../plugins/buildMessageSegments';

const MAX_MESSAGE_HISTORY_SIZE = 50;

export type MessageHistoryMentionSnapshot = {
  id: string;
  label: string;
  type?: 'parent' | 'child' | 'action' | 'resource';
  mentionEntity?: MentionEntity;
};

export type MessageHistoryEntry = {
  editorStateJson: string;
  plainText: string;
  mentions: MessageHistoryMentionSnapshot[];
  editorMentions: MentionEntityWithOffset[];
};

export type MessageHistoryNavigation = {
  pushMessage: (entry: MessageHistoryEntry) => void;
  navigateUp: ({
    currentEntry,
  }: {
    currentEntry: MessageHistoryEntry;
  }) => MessageHistoryEntry | null;
  navigateDown: ({
    currentEntry,
  }: {
    currentEntry: MessageHistoryEntry;
  }) => MessageHistoryEntry | null;
  resetNavigation: () => void;
  isBrowsingHistory: () => boolean;
};

export function createMessageHistoryController({
  initialEntries = [],
  onHistoryChanged,
}: {
  initialEntries?: MessageHistoryEntry[];
  onHistoryChanged?: (entries: MessageHistoryEntry[]) => void;
} = {}): MessageHistoryNavigation {
  const history: MessageHistoryEntry[] = [...initialEntries];
  let historyIndex = -1;
  let draft: MessageHistoryEntry | null = null;

  const notifyHistoryChanged = () => {
    onHistoryChanged?.([...history]);
  };

  const pushMessage = (entry: MessageHistoryEntry) => {
    const trimmed = entry.plainText.trim();
    if (!trimmed) {
      return;
    }

    const lastEntry = history[history.length - 1];
    if (lastEntry?.plainText !== trimmed) {
      history.push({
        editorStateJson: entry.editorStateJson,
        plainText: trimmed,
        mentions: entry.mentions,
        editorMentions: entry.editorMentions,
      });
      if (history.length > MAX_MESSAGE_HISTORY_SIZE) {
        history.shift();
      }
    }

    historyIndex = -1;
    draft = null;
    notifyHistoryChanged();
  };

  const navigateUp = ({
    currentEntry,
  }: {
    currentEntry: MessageHistoryEntry;
  }): MessageHistoryEntry | null => {
    if (history.length === 0) {
      return null;
    }

    if (historyIndex === -1) {
      draft = currentEntry;
      historyIndex = history.length - 1;
      return history[historyIndex];
    }

    if (historyIndex <= 0) {
      return null;
    }

    historyIndex -= 1;
    return history[historyIndex];
  };

  const navigateDown = ({
    currentEntry: _currentEntry,
  }: {
    currentEntry: MessageHistoryEntry;
  }): MessageHistoryEntry | null => {
    if (historyIndex === -1) {
      return null;
    }

    if (historyIndex < history.length - 1) {
      historyIndex += 1;
      return history[historyIndex];
    }

    historyIndex = -1;
    return draft;
  };

  const resetNavigation = () => {
    historyIndex = -1;
    draft = null;
  };

  const isBrowsingHistory = () => historyIndex !== -1;

  return {
    pushMessage,
    navigateUp,
    navigateDown,
    resetNavigation,
    isBrowsingHistory,
  };
}
